import type { ConsignmentInvoiceState, DueRule, KycStatus, PaymentGate } from '@miraki/contracts';
/**
 * The shapes the API actually returns.
 *
 * Request bodies are typed by `@miraki/contracts`, which both sides share.
 * Responses are declared here because they are projections chosen per screen,
 * not schemas — the retailer list and the retailer detail are different views
 * of one row, and neither is "the retailer type".
 */

export interface Portal {
  id: string;
  code: string;
  name: string;
  baseCurrency: string;
  capabilities: Record<string, boolean>;
}

/**
 * RL-05 — one entry of the menu, decided by the server.
 *
 * The nav used to be a compiled array here, which meant re-gating a screen or
 * giving a role one more section was a release. The client asked for page and
 * function access to be configurable, so the list arrives with the session and
 * this file only says what an entry looks like.
 */
export interface VisiblePage {
  id: string;
  route: string;
  name: string;
  module: string;
  sortOrder: number | null;
  capability: string | null;
  /** The permission the screen is useless without, named in a refusal. */
  permissionCode: string | null;
  /** Whether this session may open it. Only granted pages reach the menu. */
  granted: boolean;
}

export interface InternalMe {
  kind: 'internal';
  id: string;
  email: string;
  name: string;
  /** SA-01 — true means every permission, including ones not yet written. */
  isSuperAdmin: boolean;
  permissions: string[];
  pages: VisiblePage[];
  portals: Portal[];
}

export interface RetailerMe {
  kind: 'retailer';
  id: string;
  email: string;
  name: string;
  isPrimary: boolean;
  termsAcceptedAt: string | null;
  /** A revision of the payment terms awaiting acceptance; the server locks the portal while set. */
  paymentTermsPending: {
    version: number; stages: Array<{ gates: PaymentGate; pct: string }>; reason: string | null;
  } | null;
  /**
   * CR-57 / D105 — where this account stands on KYC. Gates SUBMITTING AN ORDER, nothing else:
   * browsing and the cart work in every state. PENDING covers both "no pack yet" and "awaiting
   * review"; REJECTED carries `kycRejectionReason` so the retailer knows what to fix.
   */
  kycStatus: KycStatus;
  kycRejectionReason: string | null;
  /** ON-06 — true for a credential the onboarding-approval flow generated,
   *  until the retailer changes it. The server refuses every other retailer
   *  route while this is true (`RetailerGuard`) — the client redirects on it
   *  for the experience, not as the actual gate. */
  mustChangePassword: boolean;
  /** RL-05 — Owner, Buyer or Accounts. Never 'all': there is no retailer-side
   *  Super Admin, and the account boundary is enforced server-side regardless. */
  permissions: string[];
  pages: VisiblePage[];
  retailer: { id: string; name: string; status: RetailerStatus; tradingCurrency: string };
  portal: Portal | null;
}

/**
 * A discriminated union, so the app routes on `kind`.
 *
 * The two populations are genuinely different — a retailer has one account and
 * no permissions; a staff member has permissions across portals — and modelling
 * them as one optional-heavy shape would push `if (me.retailer)` into every
 * screen.
 */
export type Me = InternalMe | RetailerMe;

/**
 * One sellable variant as a retailer sees it.
 *
 * This described the pre-CR-53 shape long after the API stopped sending it —
 * `metal` and `stone` had become a purity and a colour held by id, and
 * `minimumOrderQuantity` was removed outright by CR-07. Because nothing checks a
 * hand-written type against the response, the catalog rendered two em-dashes and
 * a blank column and TypeScript was satisfied throughout.
 *
 * There is no availability field and no minimum order quantity (D4, CR-07).
 */
export interface ShopVariant {
  /** The full variant code — the key for cart, order lines and routes. Never displayed to a retailer. */
  code: string;
  /** The design's own code (e.g. HP-403584) — what a retailer is shown. */
  baseCode: string;
  /** The design's name, denormalised by the API so the list needs no second call. */
  product: string | null;
  collection: string | null;
  category: string | null;
  /** Trade labels, never ids: 18k, Yellow. */
  purity: string | null;
  metalColour: string | null;
  /** CR-53 — the admin-defined display order (`mki_attribute_values.sort_order`).
   *  Sort by these, never by the label: parsing "18k" as a number and sorting
   *  descending, or sorting "Rose"/"White"/"Yellow" alphabetically, both land
   *  backwards from what Admin actually set. */
  puritySortOrder: number | null;
  metalColourSortOrder: number | null;
  caratCode: string | null;
  /**
   * Stone — a variant-defining axis, the same standing as purity/metal
   * colour above, not the product-level RANGE `stones` below describes.
   * Labels, never ids, for the same reason purity/metalColour are. Null on a
   * plain metal-only variant.
   */
  /** Lab Grown or Natural — an ORIGIN, priced, and its own axis. */
  stoneOrigin: string | null;
  gemstone: string | null;
  stoneShape: string | null;
  stoneCut: string | null;
  stoneColour: string | null;
  stoneClarity: string | null;
  /** CR-47 — the trade asks three different weight questions. Decimal strings. */
  netMetalWeightG: string | null;
  grossWeightG: string | null;
  totalCaratWeight: string | null;
  listPrice: string;
  /**
   * PR-02 — the RP, shown to the retailer beside the wholesale price. Decimal
   * string like every other money field (NF-04).
   *
   * Nullable only for a variant written before migration 0032 backfilled it; a
   * screen should render a dash rather than assume a figure. PR-03 — never total
   * this into anything the retailer owes.
   */
  rpPrice: string | null;
  attributes: Record<string, unknown>;
  /** This retailer's own reference for the piece, if they've set one. Read-only here. */
  externalSku: string | null;
  /** CR-43 — already scoped to this variant's own metal colour by the API, most
   *  relevant first. Empty, never missing, when the product has no photographs. */
  imageUrls: string[];
}

/** One metal a product is made in, and the piece that stands for it — see
 *  `metalOptionsByProduct` in `shop.controller.ts`. */
export interface ShopMetalOption {
  purity: string | null;
  metalColour: string | null;
  puritySortOrder: number | null;
  metalColourSortOrder: number | null;
  code: string;
  listPrice: string;
}

/** `GET /shop/catalog` — one card per product, with every metal it comes in. */
export interface ShopCatalogCard extends ShopVariant {
  metals: ShopMetalOption[];
}

/**
 * `GET /shop/catalog/:code` only — `GET /shop/catalog` (the list) returns plain
 * `ShopVariant[]` with none of these three, so they live on their own type
 * rather than as optional fields a list row would never actually carry.
 */
export interface ShopVariantDetail extends ShopVariant {
  /** R-03 — the lowest price among this design's published variants. Null with none. */
  startingPrice: string | null;
  /** PC-04/CT-04 — every published variant of the same product, this one
   *  included, for the switcher. Real rows, never a computed price (README).
   *  `attributes` carries whatever Admin set beyond purity/colour (e.g.
   *  `gemstone`, `diamondType`) so the switcher can group on it when it
   *  genuinely varies across this product's own siblings. */
  siblings: Array<{
    code: string; purity: string | null; metalColour: string | null; listPrice: string;
    /** PR-02 — the switcher shows both prices, so the siblings carry both. */
    rpPrice: string | null;
    puritySortOrder: number | null; metalColourSortOrder: number | null;
    attributes: Record<string, unknown>;
    caratCode: string | null;
    /** Stone, as a real variant axis — see the note on `ShopVariant` above. */
    stoneOrigin: string | null;
    gemstone: string | null; stoneShape: string | null; stoneCut: string | null;
    stoneColour: string | null; stoneClarity: string | null;
    /** CR-47 — so the Metal & fit panel can describe whichever sibling the
     *  choices resolve to. Decimal strings. */
    netMetalWeightG: string | null; grossWeightG: string | null; totalCaratWeight: string | null;
  }>;
  /** CR-44 — the sizes this design can be made in. Empty for a category with no sizing. */
  sizeOptions: string[];
  /** CR-23 — same collection first, then same category, then anything else, capped at 4. */
  related: Array<{ code: string; baseCode: string; product: string | null; listPrice: string; imageUrls: string[] }>;
  /** CR-48/CR-49 — product-level, already admin-visible; a field is `null` when
   *  that attribute doesn't apply to this category (e.g. `clasp` on a ring). */
  make: {
    setting: string | null; finish: string | null; clasp: string | null;
    backing: string | null; hallmark: string | null;
  };
  /** CR-48 — named rows, not fixed columns (a band width, a chain width, a drop). */
  dimensions: Array<{ label: string; millimetres: string }>;
  /**
   * CR-43 — the design's stone spec, as a RANGE the design is built to, not the
   * exact grading of this one sellable piece (that column doesn't exist
   * anywhere in the schema — see the backend's own comment on this query).
   */
  stones: Array<{
    position: string; shape: string | null; stoneCount: number;
    caratSpec: string | null; cutSpec: string | null; colourSpec: string | null;
    claritySpec: string | null; isCertified: boolean;
  }>;
  /**
   * Stone as a descriptive choice, not a priced axis — every Material/Shape/
   * Cut/Colour/Clarity value Admin has selected and saved for this design
   * (`ProductDetail.tsx`'s "Stone" block), one label list per field. Empty
   * THE OPTIONS ARE THE PRICED VARIANTS. Stone is a priced axis (CR-43), so a
   * value appears here when a real published sibling carries it — not because
   * Admin marked the design as offered in it. Empty for a design with no stone,
   * and the screen renders nothing: an empty list means "no such choice", never
   * "out of stock" (D4).
   */
  stoneOptions: {
    stoneOrigin: string[];
    gemstone: string[]; stoneShape: string[]; stoneCut: string[];
    stoneColour: string[]; stoneClarity: string[];
  };
}

export interface ShopAccount {
  name: string;
  city: string | null;
  countryCode: string;
  status: RetailerStatus;
  tradingCurrency: string;
  registration: { label: string; value: string | null };
  /**
   * D75 — the due rule in force for this account, resolved account → portal by the API.
   *
   * Outside `terms`, which is null for an account with no trading-terms row: such an account
   * still has a due rule, the portal's. Read this for any "when is payment due" sentence —
   * `terms.paymentTermsDays` below is the stored box, not the rule.
   */
  dueRule: DueRule;
  terms: {
    /** Null means "no term of its own"; read `dueRule` above for the rule that applies. */
    paymentTermsDays: number | null; advancePct: string;
    /** The budget Admin assigned (BG-01) — null until one is set. */
    creditLimit: string | null; creditLimitCurrency: string;
    incoterm: string; accountManager: string | null;
  } | null;
  you: { name: string; email: string; isPrimary: boolean };
  registeredAddress: ShopAddress | null;
  billingAddress: ShopAddress | null;
  deliveryAddresses: ShopAddress[];
  accountManagerContact: { name: string; email: string | null } | null;
  team: Array<{
    name: string; email: string | null; roles: string[];
    isPrimary: boolean; isActive: boolean; isYou: boolean;
  }>;
}

export interface ShopAddress {
  id: string;
  line1: string | null; line2: string | null; city: string | null; postalCode: string | null;
  countryCode: string | null; contactName: string | null; contactPhone: string | null;
  isDefault: boolean;
}

export interface StatementLine {
  date: string; type: string; reference: string;
  debit: string | null; credit: string | null; balance: string; currency: string;
}

export interface Statement {
  currency: string | null;
  lines: StatementLine[];
  closingBalance: string;
  note: string | null;
}

/**
 * R-04/R-05 — the cart. Mirrors `CartLineOut`/`CartOut` in
 * `apps/api/src/cart/cart.service.ts`. No price is ever posted from here
 * (invariant 6, ordering-cart implementation spec §1) — `listPrice` and
 * `lineTotal` are read-only, recomputed server-side on every `GET`.
 *
 * `isPublished` is always `true` in what a `GET` returns — an unpublished
 * variant's line is dropped from the response entirely (invariant 7), not
 * sent with a flag. The field stays here because dropping it silently once a
 * screen only checked the length would be an easy way to reintroduce that bug.
 */
export interface CartLine {
  variantCode: string;
  baseCode: string;
  quantity: number;
  name: string | null;
  listPrice: string;
  lineTotal: string;
  isPublished: boolean;
  /** CR-44/CR-15 — carried verbatim, never validated or priced. */
  size: string | null;
  remark: string | null;
  /** CR-43 — single representative photo, scoped to this line's own metal
   *  colour. `null` when the product has no photographs. */
  imageUrl: string | null;
  /** Metal then stone in words, e.g. "14k · Yellow · Lab Grown · Diamond · SI1". */
  spec: string | null;
}

export interface Cart {
  lines: CartLine[];
  total: string;
  currency: string;
}

/**
 * CT-08 — "order again". Shape assumed for `GET /shop/last-order`; a parallel
 * session owns that endpoint. Report back if it lands with a different shape.
 */
export interface LastOrderLine {
  variantCode: string;
  quantity: number;
  /** CR-44 — carried so "order again" can offer the same size back. */
  size: string | null;
}

export interface LastOrder {
  lines: LastOrderLine[];
}

/**
 * R-06/A-14 — mirrors `OrderService.list()`'s row shape
 * (`apps/api/src/ordering/order.service.ts`). `status` is the effective
 * status (an expired-but-unswept order already reads EXPIRED here);
 * `storedStatus` is the literal column, kept apart so a screen can say
 * "expired" without claiming a sweep ran.
 */
export interface OrderSummary {
  id: string;
  reference: string;
  /** Names are not unique across accounts; this is what a shipment groups on (CR-26). */
  retailerId: string;
  retailer: string;
  /** Who placed it — a retailer user, null for an order that predates this column. */
  placedBy: string | null;
  /** Line count and total quantity across those lines. */
  lines: number;
  pieces: number;
  /** Distinct product names on the order (no variant codes), in line order. */
  items: string[];
  status: string;
  /** Made against ordered while the order is in manufacture or ready; null otherwise. */
  production?: { made: number; ordered: number; lines?: ProductionLine[] } | null;
  storedStatus: string;
  currency: string;
  submittedTotal: string;
  agreedTotal: string;
  submittedAt: string;
  acceptanceExpiresAt: string | null;
  expectedDeliveryDate: string | null;
  isCommittedDate: boolean;
  /** OR-14 — the retailer wrote last and Miraki has not replied (A-14's marker). */
  awaitingReply: boolean;
}

export type OrderPaymentStatus =
  | 'NOT_APPLICABLE' | 'UNPAID' | 'PENDING_VERIFICATION' | 'PARTIAL' | 'PAID';

/** `GET /shop/orders` — the shared summary plus what the retailer's own Orders page adds (R-06). */
/** One product's progress while an order is in production — what the status chip shows on hover. */
export interface ProductionLine { name: string; variantCode: string; made: number; ordered: number }

export interface RetailerOrderSummary extends OrderSummary {
  paymentStatus: OrderPaymentStatus;
  paid: string;
  /** Null on a closed order — it owes nothing. */
  balanceDue: string | null;
  /** Whoever approved or reviewed the order, else the account manager; null when neither exists. */
  orderContact: string | null;
  lastUpdatedAt: string | null;
  /** Retailer-facing states only: dispatched is SHIPPED, received is DELIVERED. */
  shipments: Array<{
    id: string; status: 'SHIPPED' | 'DELIVERED'; reference: string;
    carrier: string | null; trackingReference: string | null;
  }>;
  deliveredAt: string | null;
  invoices: Array<{ id: string; reference: string }>;
}

export interface ReorderResult { added: number; pieces: number; skipped: string[] }

/** The catalogue specification of a line's variant; every field is null when the catalogue holds no value. */
export interface OrderLineSpec {
  metalType: string | null; purity: string | null; metalColour: string | null;
  netMetalWeightG: string | null; grossWeightG: string | null; totalCaratWeight: string | null;
  centreCarat: string | null; stoneOrigin: string | null; gemstone: string | null; stoneShape: string | null;
  stoneCut: string | null; stoneColour: string | null; stoneClarity: string | null;
  setting: string | null; finish: string | null; clasp: string | null; backing: string | null;
}

export interface OrderLine {
  /** Present on the admin and retailer detail; null once the variant has left the catalogue. */
  spec?: OrderLineSpec | null;
  /** SH-01/CR-27 — what a shipment's `lines[].orderLineId` references. */
  id: string;
  lineNumber: number;
  variantCode: string;
  /** The design's own code, for retailer-facing display; null once the variant has left the catalog. */
  baseCode: string | null;
  description: string | null;
  quantity: number;
  /** Pieces of this line made so far, never more than ordered. Null unless the order is in manufacture or ready. */
  made?: number | null;
  submittedUnitPrice: string;
  agreedUnitPrice: string;
  lineTotal: string;
  size: string | null;
  /** CR-15 — the retailer's free-text note, carried verbatim from the cart
   *  line it was submitted from. Never validated or priced. */
  remark: string | null;
  /** CR-43 — same colour-scoped single photo as `CartLine.imageUrl`, joined
   *  back to the catalog by variant code. `null` once a code is repurposed
   *  or unpublished since submission, or the product has no photographs. */
  imageUrl: string | null;
}

/** Mirrors `order.service.ts`'s `REVISION_FEEDBACK_SUBJECT` — the query a
 *  retailer's "send feedback" files, so both sides can pick it out. */
export const REVISION_FEEDBACK_SUBJECT = 'Feedback on the offer';

export interface OrderQueryMessage {
  side: 'retailer' | 'miraki';
  author: string;
  body: string;
  sentAt: string;
}

export interface OrderQuery {
  id: string;
  subject: string;
  status: string;
  raisedAt: string;
  messages: OrderQueryMessage[];
}

/** R-07/A-15 — mirrors `OrderService.detail()`. `availableActions` is
 *  computed server-side from `order.machine.ts`'s own table — the screen
 *  renders one button per action rather than re-deriving which are legal. */
export interface OrderDetail {
  id: string;
  reference: string;
  retailer: { id: string; name: string };
  status: string;
  storedStatus: string;
  expired: boolean;
  currency: string;
  submittedTotal: string;
  agreedTotal: string;
  variancePct: number;
  advancePct: string | null;
  advanceAmount: string | null;
  /** MF-06 — part of the advance is not yet covered by VERIFIED proofs;
   *  `beginManufacture` is left out of `availableActions` while this holds. */
  advanceOutstanding: boolean;
  /** Pieces Manufacturing has recorded as made, against pieces ordered. Null unless in manufacturing or ready. */
  production: { made: number; ordered: number } | null;
  acceptanceExpiresAt: string | null;
  expectedDeliveryDate: string | null;
  isCommittedDate: boolean;
  submittedAt: string;
  closureReason: string | null;
  availableActions: string[];
  lines: OrderLine[];
  queries: OrderQuery[];
  /** OR-14 — every message on the order, oldest first: chat, earlier queries
   *  and revision feedback in one timeline. */
  conversation: OrderQueryMessage[];
  awaitingReply: boolean;
  /** False once the order is finished — the conversation stays readable. */
  canMessage: boolean;
}

/** SH-01 through SH-11 — mirrors `ShipmentService`. */
export type ShipmentState =
  'DRAFT' | 'CONFIRMED' | 'PACKED' | 'AWAITING_PAYMENT' | 'DISPATCHED' | 'DELIVERED' | 'CANCELLED';

export interface Shipment {
  id: string;
  reference: string;
  state: ShipmentState;
  carrier: string | null;
  trackingReference: string | null;
  incoterm: string | null;
  insuredValue: string | null;
  dutyPaidBy: string | null;
  packedAt: string | null;
  dispatchedAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  /** The consignment invoice this shipment was created from. */
  consignmentInvoiceId: string | null;
  courierCharge: string | null;
  /** @deprecated Interim design, replaced by the consignment invoice (D97). */
  invoiceReference: string | null;
  /** @deprecated see above. */
  subtotal: string | null;
  /** @deprecated see above. */
  balanceDue: string | null;
  /** @deprecated see above. */
  confirmedAt: string | null;
  /** Present only on a `dispatch` response that found unpaid orders (SH-06). */
  blockedOn?: string[];
  orderTransitions?: Array<{ orderId: string; ok: boolean; message?: string }>;
}

export interface ShipmentDetail extends Shipment {
  orders: Array<{ id: string; reference: string; status: string }>;
  lines: Array<{
    id: string; orderId: string; orderLineId: string; quantityShipped: number; description: string | null;
  }>;
}

// ------------------------------------------------------------ consignment invoices
// docs/plans/consignment-invoice-parent
// A response type is a claim, not a check:
// compare with a real payload from the API before trusting it.
//
// D97: the DESPATCH document. The PURCHASE invoice is a different document and keeps its name
// this release — everything under `payables*` below still names it, correctly.

/**
 * **Re-exported, never re-declared.** This file used to hand-write
 * `'OPEN' | 'SHIPMENT_CREATED' | 'CANCELLED'` — a second copy of the schema's enum, in a file
 * whose own header says a type here is a CLAIM, not a check. Nothing verified it, so it would
 * have gone stale silently the day SH-14's terminal state landed (D92, open).
 *
 * The one declaration is `consignmentInvoiceStates` in `packages/contracts/src/consignment-invoice.ts`,
 * which the drizzle schema also imports. D92 is therefore one line there plus one i18n key at
 * `consignmentInvoice.state.<NAME>`, and every `Record<ConsignmentInvoiceState, …>` in a screen
 * fails typecheck the moment a member is added — which is the point.
 */
export type { ConsignmentInvoiceState };
/**
 * Where the invoice's one payable stands. Mirrors `ConsignmentInvoicePaymentStatus` on the API.
 *
 * `NOT_PAYABLE` is not a shade of unpaid: nothing is payable on this despatch at all, which is
 * the ordinary case for a consignment with no courier charge (SH-06, SH-13). It exists because
 * `'0.0000'` is a truthy string and a zero payable used to read as "Payment pending".
 */
export type PaymentStatus = 'NOT_PAYABLE' | 'PENDING' | 'PART_PAID' | 'PAID';

/** One row of the Consignment Invoice list. */
export interface ConsignmentInvoiceRow {
  id: string;
  reference: string;
  state: ConsignmentInvoiceState;
  retailer: { id: string; name: string };
  orderReferences: string[];
  currency: string;
  /** The wholesale value of the goods sent. Consigned, so never a debt (SH-06). */
  consignedValue: string;
  /** What is payable on the despatch: the courier charge and nothing else (SH-13). */
  totalPayable: string;
  paymentStatus: PaymentStatus;
  /** The part of `totalPayable` not yet paid. Null for a cancelled invoice. */
  balance: string | null;
  shipment: { id: string; reference: string; state: ShipmentState } | null;
  createdAt: string;
}

/** A READY order offered on the create page, with its unclaimed lines. */
export interface ConsignmentInvoiceCandidate {
  orderId: string;
  reference: string;
  retailer: { id: string; name: string };
  currency: string;
  /** The order may still be in manufacturing: only the pieces made so far can be invoiced. */
  orderStatus?: string;
  orderAmount: string;
  lines: Array<{
    orderLineId: string; description: string | null; variantCode: string;
    /** Quantity that is ready and not yet claimed by a live consignment invoice — the most that may be invoiced. */
    quantity: number; unitPrice: string; lineAmount: string;
    /** Ordered in total / ready (made) so far / already on live invoices. */
    ordered?: number; ready?: number; invoiced?: number;
  }>;
}

export interface ConsignmentInvoiceFigures {
  /** What was sent, valued at wholesale. Consigned: title stays with Miraki (SH-06). */
  consignedValue: string;
  /** @deprecated Retired advance model (D31/D33/D34). Feeds no total and is not shown. */
  advanceRequired: string;
  /** @deprecated see above. */
  advanceReceived: string;
  /** Null until the shipment is created and the charge is known. */
  courierCharge: string | null;
  /** Always null for now: the tax treatment is undecided (D9). */
  tax: null;
  /** The courier charge, and nothing else (SH-13). Zero when there is none. */
  totalPayable: string;
  /** Courier money verified — the only money that settles the payable. */
  payableReceived: string;
  /** May be negative: a credit. */
  remaining: string;
  /** Goods money that arrived under the retired model. Settles nothing here. */
  goodsPaymentsReceived: string;
  /** @deprecated Retired advance model. Gates nothing. */
  advanceSatisfied: boolean;
  paymentStatus: PaymentStatus;
  /** True when no order or retailer term carried a percentage and the 50% default applied. */
  defaultPctApplied: boolean;
}

/** Mirrors `CourierCharge` in @miraki/contracts. */
export interface CourierChargeView {
  consignmentInvoiceId: string;
  currency: string;
  scheduled: boolean;
  anchorOrderId: string;
  shipmentReference: string | null;
  charge: string | null;
  paid: string;
  pending: string;
  outstanding: string;
  payableNow: string;
  blocksDispatch: boolean;
}

export interface ConsignmentInvoiceDetail {
  courier: CourierChargeView;
  id: string;
  reference: string;
  state: ConsignmentInvoiceState;
  cancelReason: string | null;
  retailer: { id: string; name: string };
  currency: string;
  createdAt: string;
  orders: Array<{
    orderId: string;
    reference: string;
    orderAmount: string;
    advancePct: string;
    lines: Array<{
      orderLineId: string; description: string | null; variantCode: string;
      quantity: number;
      /** The most this line can carry: what is unclaimed by other invoices plus its own quantity. */
      maxQuantity: number;
      unitPrice: string; lineAmount: string;
    }>;
  }>;
  figures: ConsignmentInvoiceFigures;
  shipment: {
    id: string; reference: string; state: ShipmentState;
    courierName: string | null; courierCharge: string | null; trackingNumber: string | null;
    dispatchedAt: string | null; deliveredAt: string | null;
    /** Present while a dispatch is held for payment (SH-06). */
    blockedOn?: string[];
  } | null;
  canCreateShipment: boolean;
  /** Plain words for why Create Shipment is not offered; null when it is. */
  cannotCreateReason: string | null;
}

// ---------------------------------------------------------------- retailer view of a consignment invoice
// What the API gives a retailer: read-only, its own company's, and built field by field on the server
// (not the staff response with things hidden). A claim, not a check: compare with a real payload.

export interface ShopConsignmentInvoiceRow {
  id: string;
  reference: string;
  createdAt: string;
  status: Exclude<ConsignmentInvoiceState, 'CANCELLED'>;
  currency: string;
  orderReferences: string[];
  /** The wholesale value of the goods sent. Consigned, so never a debt (SH-06). */
  consignedValue: string;
  /** What is payable on the despatch: the courier charge and nothing else (SH-13). */
  totalPayable: string;
  paymentStatus: PaymentStatus;
  /** The part of `totalPayable` not yet paid. */
  balance: string | null;
  shipment: { reference: string; status: ShipmentState } | null;
}

/**
 * SH-10..SH-12 — what became of a delivery's pieces.
 *
 * Money and inventory fields are OPTIONAL because the server omits them by role (DB-07): a Buyer
 * is sent no money at all and Accounts no inventory detail. Optional here is therefore the honest
 * type — it says "may not have arrived", not "may be empty". Never default a missing one to zero.
 */
export type PieceBucket = 'incoming' | 'held' | 'sold' | 'returned' | 'other';

export interface ReconciledPiece {
  jobNumber: string;
  status: string;
  bucket: PieceBucket;
  wholesaleValue: string;
  /** Inventory audiences only — what SL-01's write needs to name the piece (`zMarkSold`). */
  pieceId?: string;
  description?: string;
  soldOn?: string | null;
  returnedAt?: string | null;
  invoiceReference?: string | null;
  invoiceState?: string | null;
  /** null where nothing is invoiced — which is NOT the same as nothing outstanding. */
  outstanding?: string | null;
}

export interface Reconciliation {
  pieces: ReconciledPiece[];
  totals: {
    consigned: string;
    incoming: string;
    held: string;
    sold: string;
    returned: string;
    other: string;
    invoiced?: string;
    outstanding?: string;
  };
}

export interface ShopConsignmentInvoice {
  id: string;
  reference: string;
  createdAt: string;
  status: Exclude<ConsignmentInvoiceState, 'CANCELLED'>;
  currency: string;
  orders: Array<{
    orderId: string;
    reference: string;
    orderAmount: string;
    advancePct: string;
    lines: Array<{ description: string | null; quantity: number; unitPrice: string; lineAmount: string }>;
  }>;
  /**
   * `payableView` on the server's figures — the same fields Admin's `figures` carries, so the two
   * screens cannot show different numbers for one invoice. The retired advance figures are not
   * sent to a retailer at all: an advance cannot be required on consigned goods (SH-06).
   */
  amounts: {
    /** What was sent, valued at wholesale. Not payable (SH-06). */
    consignedValue: string;
    /** Null until Miraki has created the shipment and the charge is known. */
    courierCharge: string | null;
    /** Always null for now: the tax treatment is undecided (D9). */
    tax: null;
    /** The courier charge, and nothing else (SH-13). */
    totalPayable: string;
    payableReceived: string;
    /** May be negative: a credit. */
    remaining: string;
    goodsPaymentsReceived: string;
    paymentStatus: PaymentStatus;
  };
  /** The courier charge and what has been paid against it; `charge` is null until the shipment exists. */
  courier: CourierChargeView;
  /** Null until Miraki creates the shipment. */
  shipment: {
    reference: string;
    status: ShipmentState;
    courierName: string | null;
    courierCharge: string | null;
    trackingNumber: string | null;
    dispatchedAt: string | null;
    deliveredAt: string | null;
  } | null;
}

export interface PackingList {
  reference: string;
  carrier: string | null;
  trackingReference: string | null;
  incoterm: string | null;
  consignee: { name: string; city: string | null; countryCode: string } | null;
  orders: string[];
  lines: Array<{ description: string | null; quantity: number }>;
}

/** Payment proof — deliberately order-scoped, see `db/schema/payment.ts`'s
 *  own comment on why this is narrower than the full PY-12 invoice model. */
export type PaymentProofStatus = 'PENDING' | 'VERIFIED' | 'REJECTED';

export interface PaymentProof {
  id: string;
  /** Null only for a PURCHASE_INVOICE proof — an invoice covers pieces from several orders (CR-56 P4). */
  orderId: string | null;
  /** The purchase invoice a PURCHASE_INVOICE proof claims to pay; null for the other purposes. */
  purchaseInvoiceId?: string | null;
  amount: string;
  currency: string;
  paidOn: string;
  bankReference: string | null;
  status: PaymentProofStatus;
  rejectionReason: string | null;
  /** COURIER is the shipment's courier charge; it never counts towards an order milestone. */
  purpose: 'ORDER' | 'COURIER' | 'PURCHASE_INVOICE';
  submittedAt: string;
  verifiedAt: string | null;
  hasFile: boolean;
}

export type Audience = 'INTERNAL' | 'RETAILER';

export interface PermissionDef {
  code: string;
  module: string;
  action: string;
  description: string;
  /** RL-05 — staff or trade customer. A role may only hold its own side. */
  audience: Audience;
}

/** RL-05, CR-51 — one screen in the catalog a role can be granted. */
export interface PageDef {
  id: string;
  route: string;
  name: string;
  module: string;
  audience: Audience;
  /** The permission the screen is useless without. A floor, not the gate. */
  permissionCode: string | null;
  capability: string | null;
  sortOrder: number | null;
}

export interface Role {
  id: string;
  code: string;
  name: string;
  audience: Audience;
  isSuperAdmin: boolean;
  isProtected: boolean;
  isActive: boolean;
  discountCeilingPct: string | null;
  /** 'all' for Super Admin — a count would be a lie the day the catalog grows. */
  permissions: string[] | 'all';
  /** 'all' for Super Admin, for the same reason (SA-01). */
  pageIds: string[] | 'all';
  portalIds: string[];
  userCount: number;
}

export interface InternalUser {
  id: string;
  email: string;
  name: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  roles: Array<{ id: string; code: string; name: string }>;
  createdAt: string;
}

/**
 * A trade customer's login, as A-06 shows it.
 *
 * Portal-scoped by the API through its retailer: this list is the acting portal's logins and
 * never another's. `retailerName` is carried rather than looked up because it is the column
 * that makes the merged list readable — two people called "Priya" are told apart by the
 * account they belong to, not by their id.
 */
export interface RetailerUser {
  id: string;
  email: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  isActive: boolean;
  isPrimary: boolean;
  lastSignInAt: string | null;
  createdAt: string;
  retailerId: string;
  retailerName: string;
  retailerStatus: string;
  roles: Array<{ id: string; code: string; name: string }>;
}

/** ON-08 — an address on a retailer record. `kind` never changes once written. */
export interface RetailerAddress {
  id: string;
  retailerId: string;
  kind: 'REGISTERED' | 'BILLING' | 'SHIPPING';
  line1: string | null;
  line2: string | null;
  city: string | null;
  postalCode: string | null;
  countryCode: string | null;
  contactName: string | null;
  contactPhone: string | null;
  isDefault: boolean;
}

export interface Country {
  code: string;
  name: string;
  defaultCurrency: string;
  registrationIdLabel: string;
  registrationIdPattern: string | null;
  /**
   * ON-03 — the pattern in words: "a 15-digit number", "DE followed by 9 digits".
   *
   * The pattern itself is never shown to an applicant. Null where the pack's rule
   * cannot be put plainly, in which case the form says nothing about the shape
   * rather than printing the expression.
   */
  registrationIdFormat: string | null;
  /** ON-03 — Postcode, ZIP code, PIN code. Null where the jurisdiction has none. */
  postalCodeLabel: string | null;
  taxRegime: string;
  capabilityRestrictions: Record<string, false>;
}

export type ApplicationStatus = 'PENDING' | 'INFO_REQUESTED' | 'APPROVED' | 'REJECTED';

export interface Application {
  id: string;
  reference: string;
  businessName: string;
  contact: { name: string; email: string; phone: string | null };
  countryCode: string;
  city: string | null;
  registrationId: string;
  /** Only where the portal asks for one. */
  buyingGroup: string | null;
  status: ApplicationStatus;
  submittedAt: string;
  ageDays: number;
  documentCount: number;
  tradeReferenceCount: number;
  note: string | null;
  retailerId: string | null;
}

/** A-01's "Payments to verify" tile + table. Denormalised server-side so the
 *  dashboard needs no second call per row. */
export interface PendingPayment {
  id: string;
  purpose: 'ORDER' | 'COURIER' | 'PURCHASE_INVOICE';
  /** Null for a PURCHASE_INVOICE proof, which belongs to an invoice and not to one order (CR-56 P4). */
  orderId: string | null;
  orderReference: string | null;
  /** Set for a PURCHASE_INVOICE proof: where Admin verifies it (A-45). */
  purchaseInvoiceId: string | null;
  purchaseInvoiceReference: string | null;
  retailerName: string;
  amount: string;
  currency: string;
  submittedAt: string;
  bankReference: string | null;
  hasFile: boolean;
}

export type RetailerStatus = 'ACTIVE' | 'ON_HOLD' | 'DORMANT' | 'WIND_DOWN' | 'CLOSED';

export interface RetailerRow {
  id: string;
  name: string;
  /** Primary login address, else the business contact; null when neither exists. */
  email: string | null;
  city: string | null;
  countryCode: string;
  tier: string | null;
  status: RetailerStatus;
  tradingCurrency: string;
  /** Decimal strings, always. Never parsed into a number for display. */
  creditLimit: string | null;
  /** FN-02, per currency — AN-08 refuses a total across them. Usually one entry. */
  payables: { currency: string; amount: string }[];
  /** The stored box. Null means "no term of its own" — read `dueRule` for the rule in force. */
  paymentTermsDays: number | null;
  /** D75 — the rule in force, resolved account → portal. A-03's Terms column states this. */
  dueRule: DueRule;
  accountManager: string | null;
}

export interface RetailerDetail {
  id: string;
  name: string;
  city: string | null;
  countryCode: string;
  registration: { label: string; value: string | null };
  taxRegime: string;
  tier: string | null;
  status: RetailerStatus;
  tradingCurrency: string;
  /*
   * ON-08 — the BUSINESS contact, not a login. A login is somebody who signs in; a contact
   * is who Miraki rings, and they are routinely different people.
   */
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  /** FN-02, per currency — AN-08 refuses a total across them. Usually one entry. */
  payables: { currency: string; amount: string }[];
  /**
   * D75 — the rule in force, resolved account → portal. Outside `terms`, which is null for an
   * account with no trading-terms row and which would otherwise hide the rule for exactly the
   * accounts whose term nobody has set.
   */
  dueRule: DueRule;
  /** D75 — the portal's own rule, so A-04 can say what clearing the term box will do. */
  portalDueRule: DueRule;
  terms: {
    /** Null means "no term of its own"; the portal's rule applies. 0 means payable immediately. */
    paymentTermsDays: number | null;
    advancePct: string;
    creditLimit: string;
    creditCurrency: string;
    incoterm: string;
    accountManager: string | null;
    updatedAt: string;
  } | null;
  onboardedFrom: { reference: string; submittedAt: string } | null;
  /**
   * CR-57 / D105 — where this account stands on KYC, and who decided it. KYC gates the ORDER, not the
   * account; Admin decides the pack here (`retailer.approve`). `reviewedBy` is null until a decision.
   */
  kyc: {
    status: KycStatus;
    reviewedAt: string | null;
    reviewedBy: { id: string; name: string } | null;
    /** The last rejection's reason — kept while the retailer is back in review, cleared by an approval. */
    rejectionReason: string | null;
  };
  closedAt: string | null;
  closureReason: string | null;
}

/**
 * ON-02a, D94 — one KYC document on an application. **Metadata only; `data` never comes over
 * this wire.**
 *
 * `contentType` is what the SERVER detected from the bytes, not what the uploader declared, so
 * the icon and the label on screen describe the file rather than its name.
 *
 * `uploadedBy` is `'APPLICANT'` or the id of the internal user who filed it while chasing — the
 * screen says which, because "did they send us this or did we type it in" is a question asked of
 * the document by the person looking at it.
 */
export interface KycDocument {
  id: string;
  /** Null for a document the retailer filed themselves after signing in (CR-57 / D105). */
  applicationId: string | null;
  /** Set for a document filed against the ACCOUNT; null for one that came with the application. */
  retailerId?: string | null;
  /**
   * Where it came from — A-04 reads the pack BOTH ways (CR-57 P3). Absent on A-02's list, which is
   * the application's alone. `ACCOUNT` means the retailer filed it after signing in.
   */
  source?: 'APPLICATION' | 'ACCOUNT';
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string;
  createdAt: string;
}

/**
 * ON-02 — one trade reference. The company is the only part the applicant must give; the rest is
 * how to reach them, and which detail they have to hand varies.
 */
export interface TradeReference {
  id: string;
  applicationId: string;
  company: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  relationship: string | null;
  createdAt: string;
}


export interface AuditEntry {
  at: string;
  actor: string;
  action: string;
  object: { type: string; id: string; label: string | null };
  before: unknown;
  after: unknown;
  reason: string | null;
}

// Milestone payments — the response shapes are defined once, in `packages/contracts`
// (`milestone-payment.ts`), and re-exported here so a screen has one place to import from and
// the type cannot drift from the schema the API validates against.
export type { OrderMilestones, MilestoneStage, MilestoneProof, RefundResult, CourierCharge } from '@miraki/contracts';

/** A request to change quantity/size on order lines, as `GET …/change-requests` returns it. */
export interface OrderChangeLine {
  lineId: string; lineNumber: number; description: string | null; variantCode: string | null;
  quantityFrom: number; quantityTo: number; sizeFrom: string | null; sizeTo: string | null;
}
export type ChangeStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export interface OrderChangeRequestRow {
  id: string; status: ChangeStatus; requestedAt: string; decidedAt: string | null;
  requestedBy: string | null; decidedBy: string | null; decisionReason: string | null;
  totalWhenAsked: string | null; lines: OrderChangeLine[];
}
export interface OrderChanges {
  eligibility: { editable: boolean; reason: string | null };
  requests: OrderChangeRequestRow[];
}

/** An address as a change request carries it. */
export interface AddressShape {
  kind: 'REGISTERED' | 'BILLING' | 'SHIPPING'; addressId: string | null;
  line1: string | null; line2: string | null; city: string | null; postalCode: string | null;
  countryCode: string | null; contactName: string | null; contactPhone: string | null;
}
export interface AddressRequestRow {
  id: string; status: ChangeStatus; requestedAt: string; decidedAt: string | null;
  requestedBy: string | null; decidedBy: string | null; decisionReason: string | null;
  kind: 'REGISTERED' | 'BILLING' | 'SHIPPING' | null; addressId: string | null;
  current: AddressShape | null; proposed: AddressShape | null;
}

/* ------------------------------------------------------------------------------------
 * D97 Wave 0 — the old names, kept alive for one wave only.
 *
 * Wave 1's four sessions each migrate their own call sites against a green typecheck; none
 * of them may edit this file. **Wave 3 deletes everything below this line, in the same
 * release**, and the vocabulary gate enforces it — these names are allow-listed as
 * transitional entries, and an allow-list entry that matches nothing is itself a gate
 * failure (exit 2).
 *
 * `ConsignmentInvoiceState` is NOT aliased below on purpose: it is re-exported from
 * `@miraki/contracts` above, and nothing in the repository may hand-write that union again.
 * ----------------------------------------------------------------------------------- */

/** CR-57 / D105 — one document a retailer has filed on their own account (R-18). Never carries the bytes. */
export interface KycFiledDocument {
  id: string;
  /** Sent with the application, or added after the account existed. */
  source?: 'APPLICATION' | 'ACCOUNT';
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

/** `GET /shop/kyc-documents` — where the retailer's own pack stands, and what has been filed. */
export interface KycPack {
  kycStatus: KycStatus;
  /** Admin's reason for the last rejection. Kept while the account is back in review, so the retailer still reads it. */
  kycRejectionReason: string | null;
  /** Whether filing or removing will work right now. Always true: changing an approved pack sends it back for review. */
  mayFile: boolean;
  documents: KycFiledDocument[];
  documentsAccepted: number;
  remaining: number;
}
